import ProjectWorld, { ProjectLocalEvent } from "@/ProjectWorld";
import { injectable, inject } from "tsyringe";
import { TOKEN_ILocalData, type ILocalData } from "./LocalData";
import { TOKEN_IProjectWorldCache, type IProjectWorldCache } from "./ProjectWorldCache";
import log from 'loglevel';
import { encryptedProject } from "@utilitydelta/nosignup-js";

export const TOKEN_IProjectWorldReader = Symbol('IProjectWorldReader');

export interface IProjectWorldReader {
    lazyMaybeGet(projectId: string): Promise<ProjectWorld | undefined>;
    mustGet(projectId: string): Promise<ProjectWorld>;
    eventsNotOnServer(projectId: string, fromLi: number): Promise<ProjectLocalEvent[]>;
    allEvents(projectId: string): Promise<ProjectLocalEvent[]>;
    isExistsLocally(projectId: string): Promise<boolean>;
    getMostRecentProjectId(): Promise<string | undefined>;
}

@injectable()
export class ProjectWorldReader implements IProjectWorldReader {
    constructor(
        @inject(TOKEN_ILocalData) private localData: ILocalData,
        @inject(TOKEN_IProjectWorldCache) private projectWorldCache: IProjectWorldCache,
    ) { }

    getMostRecentProjectId = async (): Promise<string | undefined> => {
        const mostRecentProject = await this.localData.store.getMostRecentAggregate();
        return mostRecentProject?.id;
    }

    isExistsLocally = async (projectId: string): Promise<boolean> => {
        return (await this.localData.store.getAggregateById(projectId)) !== undefined;
    }

    lazyMaybeGet = async (projectId: string): Promise<ProjectWorld | undefined> => {
        if (this.projectWorldCache.isLoaded(projectId)) {
            return this.projectWorldCache.get(projectId);
        }

        const world = await this.getProjectWorldNoCache(projectId);
        if (world === undefined) return undefined;

        this.projectWorldCache.upsert(world);
        return world;
    }

    getProjectWorldNoCache = async (projectId: string): Promise<ProjectWorld | undefined> => {
        // Load aggregate and all entities in a single transaction
        const { aggregate: metadata, entities } = await this.localData.store.getAggregateWithEntities(projectId);

        if (!metadata) {
            log.warn("Project does not exist on local:", projectId);
            return undefined;
        }

        // Create new world instance
        const world = new ProjectWorld(metadata);
        if (metadata.symmetricKey !== undefined) {
            world.encryptedProject = await encryptedProject(metadata.symmetricKey);
        }

        // Populate the world with all entities
        world.coloredSquares.upsertMany(entities.coloredSquares);
        world.accessEntries.upsertMany(entities.accessEntries);
        world.shareLinks.upsertMany(entities.shareLinks);
        world.projectUsers.upsertMany(entities.projectUsers);

        return world;
    }

    mustGet = async (projectId: string): Promise<ProjectWorld> => {
        const world = await this.lazyMaybeGet(projectId);
        if (world === undefined) throw new Error(`Project world not found for project ID: ${projectId}`);
        return world;
    }

    eventsNotOnServer = async (projectId: string, fromLi: number): Promise<ProjectLocalEvent[]> => {
        return await this.localData.store.getServerEvents(projectId, 0, 0, fromLi + 1, undefined);
    }

    allEvents = async (projectId: string): Promise<ProjectLocalEvent[]> => {
        return await this.localData.store.getEvents<ProjectLocalEvent>(projectId, undefined, undefined);
    }
}